<template>
	<!-- 支付弹窗 -->
	<div class="payment-mask">
		<!-- 没有工程师时的提示 -->
		<div class="ifgcs" v-if="!project.engineerUserId">
			<div class="ifgcs-content">
				<div class="ifgcs-icon">⚠️</div>
				<div class="ifgcs-title">暂不能支付</div>
				<div class="ifgcs-desc">平台还没有调度工程师，暂不能支付。</div>
				<div class="ifgcs-footer">
					<el-button type="primary" @click="handleClose">关闭</el-button>
				</div>
			</div>
		</div>

		<!-- 支付确认提示 -->
		<div class="shuoming" v-else-if="showConfirm">
			<div class="shuoming-content">
				<div class="shuoming-icon">📋</div>
				<div class="shuoming-title">支付确认</div>
				<div class="shuoming-desc">已确认与工程师进行了沟通，资料完整，可以开始工作了。</div>
				<div class="shuoming-tips">
					<div class="tip-item">• 工程师：{{ project.engineerUserName }}</div>
					<div class="tip-item">• 项目金额：¥{{ getDisplayAmount() }}</div>
					<div class="tip-item">• 支付后请尽快联系工程师开始工作</div>
				</div>
				<div class="shuoming-footer">
					<el-button type="primary" @click="handleConfirmPay">确认支付</el-button>
					<el-button @click="handleCancelConfirm">取消</el-button>
				</div>
			</div>
		</div>

		<!-- 支付弹窗主内容 -->
		<div class="payment-dialog" v-else>
			<!-- 弹窗头部 -->
			<div class="payment-header">
				<div class="payment-title">项目费用支付</div>
				<div class="payment-close" @click="handleCancel" v-if="!isPolling">×</div>
			</div>

			<!-- 弹窗内容 -->
			<div class="payment-content">
				<!-- 二维码展示区域 -->
				<div class="qrcode-container" v-if="!paymentSuccess">
					<div class="qrcode-title">请使用微信扫描二维码完成支付</div>
					<div class="qrcode-wrapper">
						<img :src="qrcodeUrl" alt="支付二维码" v-if="qrcodeUrl" />
						<div class="loading" v-else>正在生成支付二维码...</div>
					</div>
					<div class="amount">支付金额: ¥{{ paymentAmount }}</div>
					<div class="status-tip" v-if="pollingStatus">
						支付状态: {{ getStatusText(pollingStatus) }}
					</div>
				</div>

				<!-- 支付成功提示 -->
				<div class="success-container" v-else>
					<div class="success-icon">
						✓
					</div>
					<div class="success-text">支付成功！</div>
					<div class="success-desc">项目费用已支付完成</div>
				</div>
			</div>

			<!-- 底部按钮 -->
			<div class="payment-footer">
				<button class="cancel-btn" v-if="!paymentSuccess" @click="handleCancel">
					取消支付
				</button>
				<button class="confirm-btn" v-else @click="handleConfirm">
					确定
				</button>
			</div>
		</div>
	</div>
</template>

<script>
	import {
		payProject,
		checkPayStatus
	} from "../../utils/request.js";

	export default {
		name: "PaymentDialog",
		props: {
			project: {
				type: Object,
				default: null
			}
		},
		data() {
			return {
				dialogVisible: false,
				qrcodeUrl: "", // 二维码图片URL
				paymentNum: "", // 临时支付编码
				isPolling: false, // 是否正在轮询
				pollingTimer: null, // 轮询定时器
				pollingStatus: "", // 轮询状态
				paymentSuccess: false, // 支付是否成功
				currentProjectId: '',
				currentAmount: '',
				showConfirm: true, // 控制确认提示显示
				hasEngineer: false // 是否有工程师
			};
		},
		computed: {
			// 格式化支付金额
			paymentAmount() {
				return parseFloat(this.currentAmount).toFixed(2);
			}
		},
		watch: {
			// 监听project变化
			project: {
				immediate: true,
				handler(newVal) {
					if (newVal) {
						this.hasEngineer = !!newVal.engineerUserId;
						// 如果有工程师，显示确认提示
						this.showConfirm = this.hasEngineer;
					}
				}
			}
		},
		created() {
			// 初始化时检查工程师状态
			this.hasEngineer = this.project && !!this.project.engineerUserId;
			this.showConfirm = this.hasEngineer;
		},
		methods: {
			// 获取显示金额（带元）
			getDisplayAmount() {
				if (!this.project || !this.project.projectAmount) return '0元';
				return this.project.projectAmount;
			},

			// 关闭弹窗（没有工程师时）
			handleClose() {
				this.$emit("close", false);
				this.$emit("paymentCancel");
			},

			// 确认支付（进入二维码支付）
			handleConfirmPay() {
				this.showConfirm = false;
				this.initPayment();
			},

			// 取消确认
			handleCancelConfirm() {
				this.$emit("close", false);
				this.$emit("paymentCancel");
			},

			// 初始化支付
			async initPayment() {
				if (!this.project || !this.project.projectId) {
					this.$message.error('项目信息不完整，无法支付');
					this.handleCancel();
					return;
				}

				// 从项目对象中提取金额（去掉"元"字）
				const amountStr = this.project.projectAmount?.replace('元', '') || '0';

				this.currentProjectId = this.project.projectId;
				this.currentAmount = amountStr;
				// this.currentAmount = '1' // 测试金额

				try {
					// 生成随机支付编码
					this.paymentNum = this.generatePaymentNum();

					// 调用支付接口
					const response = await payProject({
						amount: this.paymentAmount,
						projectId: this.currentProjectId,
						num: this.paymentNum
					});

					if (response.code === 200) {
						// 将支付链接转换为二维码图片URL
						this.qrcodeUrl = this.generateQrCodeUrl(response.data);
						// 开始轮询支付状态
						this.startPolling();
					} else {
						this.$message.error(response.msg || "支付二维码生成失败");
						this.handleCancel();
					}
				} catch (error) {
					// console.error("支付初始化失败:", error);
					this.$message.error("支付初始化失败，请重试");
					this.handleCancel();
				}
			},

			// 生成随机支付编码
			generatePaymentNum() {
				const timestamp = Date.now().toString();
				const randomStr = Math.random().toString(36).substring(2, 10);
				return `${timestamp}_${randomStr}`;
			},

			// 生成二维码图片URL
			generateQrCodeUrl(payUrl) {
				return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(payUrl)}`;
			},

			// 开始轮询支付状态
			startPolling() {
				this.isPolling = true;
				this.pollingStatus = "PAYING";
				this.pollingTimer = setInterval(this.checkPaymentStatus, 3000); // 每3秒检查一次
			},

			// 停止轮询
			stopPolling() {
				if (this.pollingTimer) {
					clearInterval(this.pollingTimer);
					this.pollingTimer = null;
				}
				this.isPolling = false;
			},

			// 检查支付状态
			async checkPaymentStatus() {
				try {
					const response = await checkPayStatus(this.paymentNum);


					if (response.code === 200) {
						this.pollingStatus = response.data;

						if (response.data === "SUCCESS") {
							// 支付成功
							this.handlePaymentSuccess();
						} else if (response.data === "FAIL") {
							// 支付失败，继续轮询等待
							// console.log("支付失败，继续等待...");
							// this.$message.error("支付失败，请重试");
							// this.stopPolling();
						}
						// PAYING状态继续轮询
					} else {
						// console.error("支付状态查询失败:", response.msg);
					}
				} catch (error) {
					// console.error("支付状态查询异常:", error);
				}
			},

			// 支付成功处理
			handlePaymentSuccess() {
				this.paymentSuccess = true;
				this.stopPolling();
				this.$message.success("支付成功！");

				// 延迟关闭弹窗，让用户看到成功提示
				setTimeout(() => {
					this.$emit("paymentSuccess", this.currentProjectId);
					this.$emit("close", false);
				}, 2000);
			},

			// 取消支付
			handleCancel() {
				// if (this.isPolling) {
				// 	this.$message.info('支付进行中，无法取消');
				// 	return;
				// }
				this.stopPolling();
				this.dialogVisible = false;
				this.$emit("close", false);
				this.$emit("paymentCancel");
			},

			// 确认支付完成
			handleConfirm() {
				this.$emit("close", false);
				this.$emit("paymentSuccess", this.currentProjectId);
			},

			// 获取状态文本
			getStatusText(status) {
				const statusMap = {
					"SUCCESS": "支付成功",
					"FAIL": "等待支付",
					"PAYING": "等待支付"
				};
				return statusMap[status] || "未知状态";
			}
		},
		beforeUnmount() {
			// 组件销毁前停止轮询
			this.stopPolling();
		}
	};
</script>

<style scoped>
	.payment-mask {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 9999;
	}

	/* 没有工程师的样式 */
	.ifgcs {
		width: 400px;
		background: white;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		overflow: hidden;
	}

	.ifgcs-content {
		padding: 30px;
		text-align: center;
	}

	.ifgcs-icon {
		font-size: 48px;
		margin-bottom: 16px;
	}

	.ifgcs-title {
		font-size: 18px;
		font-weight: bold;
		color: #e6a23c;
		margin-bottom: 12px;
	}

	.ifgcs-desc {
		font-size: 14px;
		color: #606266;
		margin-bottom: 24px;
		line-height: 1.5;
	}

	.ifgcs-footer {
		display: flex;
		justify-content: center;
	}

	/* 支付确认提示样式 */
	.shuoming {
		width: 450px;
		background: white;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		overflow: hidden;
	}

	.shuoming-content {
		padding: 30px;
	}

	.shuoming-icon {
		font-size: 48px;
		text-align: center;
		margin-bottom: 16px;
	}

	.shuoming-title {
		font-size: 18px;
		font-weight: bold;
		color: #409eff;
		text-align: center;
		margin-bottom: 12px;
	}

	.shuoming-desc {
		font-size: 14px;
		color: #606266;
		text-align: center;
		margin-bottom: 20px;
		line-height: 1.5;
	}

	.shuoming-tips {
		background: #f8f9fa;
		border-radius: 6px;
		padding: 16px;
		margin-bottom: 24px;
	}

	.tip-item {
		font-size: 13px;
		color: #606266;
		margin-bottom: 8px;
		line-height: 1.4;
	}

	.tip-item:last-child {
		margin-bottom: 0;
		color: #e6a23c;
		font-weight: 500;
	}

	.shuoming-footer {
		display: flex;
		justify-content: center;
		gap: 12px;
	}

	/* 支付弹窗主样式 */
	.payment-dialog {
		width: 420px;
		background: white;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		overflow: hidden;
	}

	.payment-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 20px;
		border-bottom: 1px solid #e8e8e8;
		background: #fafafa;
	}

	.payment-title {
		font-size: 16px;
		font-weight: bold;
		color: #333;
	}

	.payment-close {
		font-size: 20px;
		color: #999;
		cursor: pointer;
		width: 24px;
		height: 24px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
	}

	.payment-close:hover {
		background: #f0f0f0;
		color: #666;
	}

	.payment-content {
		padding: 20px;
	}

	.qrcode-container {
		text-align: center;
	}

	.qrcode-title {
		font-size: 16px;
		margin-bottom: 15px;
		color: #333;
	}

	.qrcode-wrapper {
		width: 220px;
		height: 220px;
		margin: 0 auto 15px;
		border: 1px solid #eee;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.qrcode-wrapper img {
		max-width: 100%;
		max-height: 100%;
	}

	.loading {
		color: #999;
	}

	.amount {
		font-size: 18px;
		font-weight: bold;
		color: #e6a23c;
		margin-bottom: 10px;
	}

	.status-tip {
		font-size: 14px;
		color: #409eff;
	}

	.success-container {
		text-align: center;
		padding: 20px 0;
	}

	.success-icon {
		font-size: 60px;
		color: #67c23a;
		margin-bottom: 15px;
		width: 80px;
		height: 80px;
		border: 3px solid #67c23a;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0 auto 15px;
	}

	.success-text {
		font-size: 18px;
		font-weight: bold;
		color: #67c23a;
		margin-bottom: 10px;
	}

	.success-desc {
		color: #909399;
		font-size: 14px;
	}

	.payment-footer {
		padding: 16px 20px;
		border-top: 1px solid #e8e8e8;
		display: flex;
		justify-content: center;
		gap: 12px;
	}

	.cancel-btn,
	.confirm-btn {
		padding: 8px 16px;
		border: 1px solid #dcdfe6;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.3s;
	}

	.cancel-btn {
		background: white;
		color: #606266;
	}

	.cancel-btn:hover:not(:disabled) {
		border-color: #c6e2ff;
		background-color: #ecf5ff;
		color: #409eff;
	}

	.cancel-btn:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.confirm-btn {
		background: #409eff;
		color: white;
		border-color: #409eff;
	}

	.confirm-btn:hover {
		background: #66b1ff;
		border-color: #66b1ff;
	}
</style>